import { useCallback } from 'react';
import { transactionsService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { Transaction } from '../types';

export function useTransactions(
  options?: ResourceQueryOptions
): ResourceState<Transaction> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      transactionsService.getAll(opts) as unknown as Promise<{ data: Transaction[]; meta?: any }>,
    []
  );
  return useResourceCollection<Transaction>(fetchFn, options);
}

export function useTransaction(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<Transaction> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (transactionId: number, opts?: ResourceQueryOptions) =>
      transactionsService.getById(transactionId, opts) as unknown as Promise<Transaction>,
    []
  );
  return useResourceById<Transaction>(fetchFn, numericId, options);
}

export function useCreateTransaction(
  onSuccess?: (data: Transaction) => void
): MutationState<Transaction> {
  const createFn = useCallback(
    (data: any) => transactionsService.create(data) as unknown as Promise<Transaction>,
    []
  );
  return useCreateResource<Transaction>(createFn, onSuccess);
}

export function useUpdateTransaction(
  onSuccess?: (data: Transaction) => void
): MutationState<Transaction> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      return transactionsService.update(numericId, data) as unknown as Promise<Transaction>;
    },
    []
  );
  return useUpdateResource<Transaction>(updateFn, onSuccess);
}

/**
 * Delete transaction using documentId
 * @param data - Full transaction object containing documentId
 */
export function useDeleteTransaction(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return transactionsService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}
